import WavesurferPlayer from '@wavesurfer/react'
import classNames from 'classnames'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {colors} from '../../../utils/colors'
import {formatDuration} from '../../../utils/date'
import renderSkeletonLoaders from '../../../utils/loader'
import './index.scss'
import {PauseIcon, PlayIconV3} from '../../../icons'

const DEFAULT_DURATION = '00:00'
const PLAY_DELAY_MS = 50
const MIN_WAVE_COUNT = 15
const MAX_WAVE_COUNT = 75
const BAR_WIDTH = 14

const VoicePreview = ({audioUrl = '', isDarkMode, isLoadingAudio, isSidebarActive}) => {
    const wavesurferRef = useRef(null)
    const containerRef = useRef(null)

    const [isPlaying, setIsPlaying] = useState(false)
    const [remaining, setRemaining] = useState(DEFAULT_DURATION)
    const [totalDuration, setTotalDuration] = useState(0)
    const [progressColor, setProgressColor] = useState(colors.colorFrostyAzure)
    const [isWavesurferReady, setIsWavesurferReady] = useState(false)
    const [dummyWaveCount, setDummyWaveCount] = useState(MIN_WAVE_COUNT)

    const iconColor = useMemo(() => {
        if (isDarkMode) return colors.colorFilterTitle
        return audioUrl ? colors.colorMuted : colors.colorFrostyAzure
    }, [isDarkMode, audioUrl])

    const durationColor = useMemo(() => {
        if (isDarkMode) return colors.colorFilterTitle
        return audioUrl ? colors.colorMuted : colors.colorFrostyAzure
    }, [isDarkMode, audioUrl])

    const updateWaveCount = useCallback(() => {
        if (containerRef.current) {
            const width = containerRef.current.offsetWidth
            const count = Math.floor(width / BAR_WIDTH) || MIN_WAVE_COUNT
            setDummyWaveCount(Math.min(count, MAX_WAVE_COUNT))
        }
    }, [])

    useEffect(() => {
        if (audioUrl) return

        updateWaveCount()
        window.addEventListener('resize', updateWaveCount)

        return () => window.removeEventListener('resize', updateWaveCount)
    }, [audioUrl, updateWaveCount])

    const handleWavesurferReady = useCallback((ws) => {
        wavesurferRef.current = ws
        setIsWavesurferReady(true)
        setIsPlaying(false)

        const duration = ws.getDuration()
        setTotalDuration(duration)
        setRemaining(formatDuration(duration))
    }, [])

    const handlePlayPause = useCallback(async() => {
        const ws = wavesurferRef.current
        if (!ws || !isWavesurferReady) return

        const context = ws.backend?.getAudioContext?.()
        if (context?.state === 'suspended') {
            await context.resume()
        }

        setTimeout(() => {
            ws.playPause()
        }, PLAY_DELAY_MS)
    }, [isWavesurferReady])

    const handlePlay = useCallback(() => {
        setIsPlaying(true)
    }, [])

    const handlePause = useCallback(() => {
        setIsPlaying(false)
    }, [])

    useEffect(() => {
        const ws = wavesurferRef.current
        if (!ws || !totalDuration) return

        const handleProgress = () => {
            const current = ws.getCurrentTime()
            const timeLeft = totalDuration - current
            setRemaining(formatDuration(timeLeft > 0 ? timeLeft : 0))
        }

        const handleFinish = () => {
            setIsPlaying(false)
            setRemaining(formatDuration(totalDuration))
            setProgressColor(colors.colorFrostyAzure)
            ws.seekTo(0)
        }

        ws.on('audioprocess', handleProgress)
        ws.on('finish', handleFinish)

        return () => {
            ws.un('audioprocess', handleProgress)
            ws.un('finish', handleFinish)
        }
    }, [totalDuration])

    useEffect(() => {
        if (!audioUrl) {
            setIsPlaying(false)
            setRemaining(DEFAULT_DURATION)
            setTotalDuration(0)
            setIsWavesurferReady(false)
            setProgressColor(colors.colorFrostyAzure)
        }
    }, [audioUrl])

    return (
        <div
            ref={containerRef}
            className={classNames('voice-preview-container', {
                'bg-blue': audioUrl,
                dark: isDarkMode
            })}
        >
            <button
                onClick={handlePlayPause}
                className={classNames('voice-preview-play', {
                    rotating: isPlaying
                })}
                type='button'
                aria-label={isPlaying ? 'Pause' : 'Play'}
                disabled={!audioUrl || isLoadingAudio}
            >
                {isPlaying ? (
                    <PauseIcon stroke={iconColor} />
                ) : (
                    <PlayIconV3 fill={iconColor} stroke={iconColor} />
                )}
            </button>

            <div className="voice-preview-wave">
                {isLoadingAudio ? (
                    renderSkeletonLoaders(1)
                ) : !audioUrl ? (
                    <div className="wave-dummy">
                        {Array.from({length: dummyWaveCount}).map((_, index) => (
                            <div key={index} />
                        ))}
                    </div>
                ) : (
                    <WavesurferPlayer
                        height={18}
                        barWidth={3}
                        barGap={3}
                        barRadius={1000}
                        waveColor={colors.colorLightBlue}
                        progressColor={progressColor}
                        cursorWidth={0}
                        normalize
                        autoplay={false}
                        url={audioUrl}
                        onReady={handleWavesurferReady}
                        onPlay={handlePlay}
                        onPause={handlePause}
                    />
                )}
            </div>

            <span
                className="voice-preview-duration"
                style={{color: durationColor}}
            >
                {isLoadingAudio ? DEFAULT_DURATION : remaining}
            </span>
        </div>
    )
}

export default VoicePreview
